import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowLeftIcon,
  CaretRightIcon,
  CheckIcon,
  ShieldWarningIcon,
  UsersIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { cn } from '@/lib/utils';
import { getGroupRoleTone } from '@/lib/utils/groups.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import { getBulkRoleImpact } from '@/lib/utils/bulk-edit-users.utils';
import { ManageUsersContent } from '@/content/ManageUsersContent';
import type { BulkEditableUser } from '@/lib/types/bulk-edit-users.types';
import type { UserRole } from '@/lib/types/database';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';

type BulkRoleChangeDrawerStep = 'pick' | 'confirm';

interface BulkRoleChangeDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: BulkEditableUser[];
  selectedUserIds: string[];
  selectedRole: UserRole | '';
  onSelectedRoleChange: (role: UserRole) => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

export function BulkRoleChangeDrawer({
  open,
  onOpenChange,
  users,
  selectedUserIds,
  selectedRole,
  onSelectedRoleChange,
  onConfirm,
  isSubmitting,
}: BulkRoleChangeDrawerProps) {
  const [step, setStep] = useState<BulkRoleChangeDrawerStep>('pick');
  const { options: roleOptions } = useRoleDirectory();
  const content = ManageUsersContent.bulkEdit.roleDrawer;
  const impact = useMemo(
    () =>
      selectedRole
        ? getBulkRoleImpact(users, selectedUserIds, selectedRole)
        : null,
    [selectedRole, selectedUserIds, users]
  );

  useEffect(() => {
    if (open) {
      setStep('pick');
    }
  }, [open]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[88vh] rounded-t-[22px]">
        <div className="mx-auto flex min-h-0 w-full max-w-sm flex-col">
          {step === 'pick' ? (
            <>
              <DrawerHeader className="pb-3">
                <DrawerHeaderRow
                  title={content.title}
                  description={content.description(selectedUserIds.length)}
                />
              </DrawerHeader>
              <div className="flex max-h-[42vh] flex-col gap-2 overflow-y-auto pr-1">
                {roleOptions.map((role) => (
                  <RoleOptionButton
                    key={role.value}
                    role={role.value}
                    label={role.label}
                    selected={selectedRole === role.value}
                    onClick={() => onSelectedRoleChange(role.value)}
                  />
                ))}
              </div>
              <DrawerFooter className="pt-4">
                <Button
                  type="button"
                  size="lg"
                  onClick={() => setStep('confirm')}
                  disabled={!selectedRole || selectedUserIds.length === 0}
                  className="bg-purple-300 text-slate-700 hover:bg-purple-200"
                >
                  {content.review}
                  <CaretRightIcon size={16} weight="bold" />
                </Button>
                <DrawerClose asChild>
                  <Button variant="outline" size="lg">
                    {content.cancel}
                  </Button>
                </DrawerClose>
              </DrawerFooter>
            </>
          ) : (
            <>
              <DrawerHeader className="pb-3">
                <DrawerHeaderRow title={content.confirmTitle} />
              </DrawerHeader>
              {impact && (
                <div className="space-y-3">
                  <p className="text-body text-slate-100">
                    {content.confirmSummary(impact.selectedCount)}{' '}
                    <RoleTransition
                      from={impact.fromRoleLabel}
                      to={getRoleLabelByKey(impact.targetRole)}
                    />
                  </p>
                  <div className="rounded-xl bg-slate-600 px-4 py-1">
                    <ImpactLine
                      icon={<CheckIcon size={15} weight="bold" />}
                      tone="green"
                    >
                      {content.willChange(
                        impact.willChangeCount,
                        getRoleLabelByKey(impact.targetRole)
                      )}
                    </ImpactLine>
                    <ImpactLine
                      icon={<UsersIcon size={15} weight="bold" />}
                      tone="sky"
                    >
                      {content.alreadyRole(
                        impact.alreadyRoleCount,
                        getRoleLabelByKey(impact.targetRole)
                      )}
                    </ImpactLine>
                    <ImpactLine
                      icon={<ShieldWarningIcon size={15} weight="bold" />}
                      tone="amber"
                      last
                    >
                      {content.permissionsWarning}
                    </ImpactLine>
                  </div>
                </div>
              )}
              <DrawerFooter className="pt-4">
                <Button
                  type="button"
                  size="lg"
                  onClick={onConfirm}
                  disabled={isSubmitting || !selectedRole}
                  className="bg-purple-300 text-slate-700 hover:bg-purple-200"
                >
                  {isSubmitting
                    ? content.loading
                    : content.confirm(selectedUserIds.length)}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => setStep('pick')}
                  disabled={isSubmitting}
                >
                  <ArrowLeftIcon size={16} weight="bold" />
                  {content.back}
                </Button>
              </DrawerFooter>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface DrawerHeaderRowProps {
  title: string;
  description?: string;
}

function DrawerHeaderRow({ title, description }: DrawerHeaderRowProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <DrawerTitle>{title}</DrawerTitle>
        {description && (
          <DrawerDescription className="text-small text-slate-300">
            {description}
          </DrawerDescription>
        )}
      </div>
      <DrawerClose asChild>
        <button
          type="button"
          className="rounded-full p-1 text-slate-400 hover:text-slate-50"
          aria-label="Close role change"
        >
          <XIcon size={18} weight="bold" />
        </button>
      </DrawerClose>
    </div>
  );
}

interface RoleOptionButtonProps {
  role: UserRole;
  label: string;
  selected: boolean;
  onClick: () => void;
}

function RoleOptionButton({
  role,
  label,
  selected,
  onClick,
}: RoleOptionButtonProps) {
  const tone = getGroupRoleTone(role);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
        selected
          ? tone.radioClassName
          : 'border-slate-550 bg-slate-600 hover:bg-slate-500/30'
      )}
    >
      <span
        className={cn(
          'grid h-5 w-5 shrink-0 place-items-center rounded-full border',
          selected ? tone.radioClassName : 'border-slate-500'
        )}
      >
        {selected && (
          <span
            className={cn('h-2 w-2 rounded-full', tone.radioDotClassName)}
          />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-body text-slate-50">{label}</span>
        <span className="block text-small text-slate-400">
          {ManageUsersContent.bulkEdit.roleDrawer.roleDescriptions[
            role as keyof typeof ManageUsersContent.bulkEdit.roleDrawer.roleDescriptions
          ] ?? ''}
        </span>
      </span>
    </button>
  );
}

interface RoleTransitionProps {
  from: string;
  to: string;
}

function RoleTransition({ from, to }: RoleTransitionProps) {
  return (
    <span className="inline-flex align-middle items-center gap-1.5">
      <span className="rounded-full border border-slate-500 bg-slate-600 px-2 py-0.5 font-field-42 text-[10px] uppercase text-slate-300">
        {from}
      </span>
      <CaretRightIcon size={12} weight="bold" className="text-slate-500" />
      <span className="rounded-full border border-purple-300/40 bg-purple-300/15 px-2 py-0.5 font-field-42 text-[10px] uppercase text-purple-300">
        {to}
      </span>
    </span>
  );
}

interface ImpactLineProps {
  icon: ReactNode;
  tone: 'green' | 'sky' | 'amber';
  children: ReactNode;
  last?: boolean;
}

function ImpactLine({ icon, tone, children, last = false }: ImpactLineProps) {
  const toneClassNames = {
    green: 'text-green-300',
    sky: 'text-sky-300',
    amber: 'text-amber-300',
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 py-3 text-body text-slate-100',
        !last && 'border-b border-slate-550'
      )}
    >
      <span className={cn('mt-0.5 shrink-0', toneClassNames[tone])}>
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}
